import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { PageHeader } from '../components/ui/PageHeader';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import { Section, ErrorPanel, LoadingRow, EmptyNote, UserLink, TableWrap, Th, Td, CapNote, Segmented, Pager, MarketTag } from '../components/ui/bits';
import { sessionTypeLabel, sentence } from '../lib/labels';
import type { SessionsResp } from '../lib/types';
import { useRpc, useSel, queryClient } from '../lib/queries';
import { supabase } from '../lib/supabase';
import { adminApi } from '../lib/adminApi';
import { agoLabel, fmtBytes, fmtDateTime } from '../lib/format';

const VIEWS = [
  { key: 'recent', label: 'All sessions' },
  { key: 'failed', label: 'Failed' },
  { key: 'orphans', label: 'Orphaned recordings' },
  { key: 'stuck', label: 'Stuck' },
  { key: 'partial', label: 'Partially linked' },
] as const;

export const SessionsPage: React.FC = () => {
  const [params, setParams] = useSearchParams();
  const view = params.get('view') ?? 'recent';

  return (
    <div>
      <PageHeader title="Sessions" subtitle="Every practice run, every failure with its real cause, and the repair buttons. Psychometric results are left out unless you pick them." />
      <div className="flex gap-1 mb-4 border-b border-stone-200">
        {VIEWS.map(v => (
          <button
            key={v.key}
            onClick={() => { const n = new URLSearchParams(params); n.set('view', v.key); setParams(n, { replace: true }); }}
            className={[
              'px-3 py-2 text-[12.5px] font-semibold border-b-2 -mb-px transition-colors',
              view === v.key ? 'border-indigo-600 text-stone-900' : 'border-transparent text-stone-500 hover:text-stone-800',
            ].join(' ')}
          >
            {v.label}
          </button>
        ))}
      </div>
      {view === 'recent' && <RecentView />}
      {view === 'failed' && <FailedView />}
      {view === 'orphans' && <OrphansView />}
      {view === 'stuck' && <StuckView />}
      {view === 'partial' && <PartialView />}
    </div>
  );
};

const TYPE_CHIPS: { value: string; label: string; types: string[] | null }[] = [
  { value: 'practice_all', label: 'All but psychometric results', types: null },
  { value: 'practice', label: 'Video interview', types: ['practice'] },
  { value: 'live', label: 'Live interview', types: ['live'] },
  { value: 'cv', label: 'CV', types: ['cv'] },
  { value: 'ac', label: 'Assessment centre', types: ['ac'] },
  { value: 'superday', label: 'Superday', types: ['superday'] },
  { value: 'assessment', label: 'Psychometric results', types: ['assessment'] },
];

const RecentView: React.FC = () => {
  const [params, setParams] = useSearchParams();
  const chip = params.get('type') ?? 'practice_all';
  const status = params.get('status') ?? 'all';
  const market = params.get('market') ?? 'all';
  const page = Math.max(1, Number(params.get('page') ?? 1));
  const types = TYPE_CHIPS.find(c => c.value === chip)?.types ?? null;
  const PER = 50;
  const { data, error, isLoading } = useRpc<SessionsResp>('admin_dash_sessions', {
    p_types: types, p_status: status === 'all' ? null : status, p_market: market === 'all' ? null : market, p_page: page, p_per: PER,
  });
  const set = (patch: Record<string, string>) => {
    const n = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) { if (v && v !== 'all' && v !== 'practice_all') n.set(k, v); else n.delete(k); }
    if (!('page' in patch)) n.delete('page');
    setParams(n, { replace: true });
  };

  return (
    <div className="space-y-3">
      {data && (
        <Section title="By type" sub="Counts from every row, internal accounts excluded." source="sessions">
          <TableWrap>
            <thead><tr><Th>Type</Th><Th className="text-right">7 days UK</Th><Th className="text-right">7 days US</Th><Th className="text-right">30 days</Th><Th className="text-right">Completed</Th><Th className="text-right">Failed</Th><Th className="text-right">All time</Th></tr></thead>
            <tbody>
              {data.by_type.map(t => (
                <tr key={t.type}>
                  <Td className="font-medium text-stone-900">{sessionTypeLabel(t.type)}</Td>
                  <Td className="text-right tabular-nums">{t.d7_uk.toLocaleString('en-GB')}</Td>
                  <Td className="text-right tabular-nums">{t.d7_us.toLocaleString('en-GB')}</Td>
                  <Td className="text-right tabular-nums">{t.d30.toLocaleString('en-GB')}</Td>
                  <Td className="text-right tabular-nums">{t.completed_30d.toLocaleString('en-GB')}</Td>
                  <Td className={`text-right tabular-nums ${t.failed_30d ? 'text-rose-700' : ''}`}>{t.failed_30d.toLocaleString('en-GB')}</Td>
                  <Td className="text-right tabular-nums text-stone-500">{t.all_time.toLocaleString('en-GB')}</Td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
          <p className="text-[12px] text-stone-500 mt-2">Psychometric results are the sessions rows a finished psychometric test writes; the runs themselves are on the Psychometrics page.</p>
        </Section>
      )}

      <Section
        title="Sessions"
        sub={data ? `${data.total.toLocaleString('en-GB')} match. Newest first.` : undefined}
        source="admin_dash_sessions"
        actions={
          <div className="flex flex-wrap gap-2">
            <Segmented label="Market" value={market} onChange={v => set({ market: v })}
              options={[{ value: 'all', label: 'UK and US' }, { value: 'uk', label: 'UK' }, { value: 'us', label: 'US' }]} />
            <Segmented label="Status" value={status} onChange={v => set({ status: v })}
              options={[{ value: 'all', label: 'Any status' }, { value: 'completed', label: 'Completed' }, { value: 'failed', label: 'Failed' }, { value: 'processing', label: 'Processing' }]} />
          </div>
        }
      >
        <div className="flex flex-wrap gap-1.5 mb-4">
          {TYPE_CHIPS.map(c => (
            <button key={c.value} onClick={() => set({ type: c.value })}
              className={`h-7 px-2.5 rounded-md text-[12.5px] font-medium border ${chip === c.value ? 'border-stone-900 bg-stone-900 text-white' : 'border-stone-200 text-stone-600 hover:text-stone-900'}`}>
              {c.label}
            </button>
          ))}
        </div>
        {isLoading && <LoadingRow />}
        {error && <ErrorPanel error={error} label="admin_dash_sessions" />}
        {data && (data.rows.length === 0 ? <EmptyNote>No sessions match</EmptyNote> : (
          <TableWrap>
            <thead><tr><Th>Session</Th><Th>User</Th><Th>Market</Th><Th>Status</Th><Th>Score</Th><Th>Length</Th><Th>When</Th></tr></thead>
            <tbody>
              {data.rows.map(s => (
                <tr key={s.id}>
                  <Td>
                    <Link to={`/sessions/${s.id}`} className="text-indigo-600 hover:underline font-medium">{s.title || sessionTypeLabel(s.type)}</Link>
                    <div className="text-[12px] text-stone-500">{sessionTypeLabel(s.type)}{s.shared ? ', shared publicly' : ''}</div>
                    {s.status === 'failed' && s.failure_reason && <div className="text-[12px] text-rose-700">{s.failure_reason}</div>}
                  </Td>
                  <Td><UserLink id={s.user_id} label={s.email} /></Td>
                  <Td><MarketTag market={s.market} /></Td>
                  <Td><Badge tone={s.status === 'completed' ? 'emerald' : s.status === 'failed' ? 'rose' : 'amber'}>{sentence(s.status)}</Badge></Td>
                  <Td className="tabular-nums">{s.overall_score ?? ''}</Td>
                  <Td className="tabular-nums whitespace-nowrap">{s.duration_seconds ? `${Math.round(s.duration_seconds / 60)} min` : ''}</Td>
                  <Td className="whitespace-nowrap">{agoLabel(s.created_at)}</Td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
        ))}
        {data && <Pager page={page} per={PER} total={data.total} onPage={p => set({ page: String(p) })} />}
      </Section>
    </div>
  );
};

const FailedView: React.FC = () => {
  const { data, error, isLoading } = useRpc<any[]>('admin_failed_sessions', { p_days: 60 });
  const [busyId, setBusyId] = useState<string | null>(null);
  const [result, setResult] = useState<Record<string, string>>({});

  const repair = async (id: string, kind: 'score' | 'transcribe') => {
    setBusyId(id);
    try {
      if (kind === 'score') await adminApi('regenerate_report', { sessionId: id });
      else {
        const r = await adminApi<{ transcribed: number; failures: number[] }>('retranscribe_session', { sessionId: id });
        setResult(prev => ({ ...prev, [id]: `transcribed ${r.transcribed} clip${r.transcribed === 1 ? '' : 's'}` }));
      }
      setResult(prev => ({ ...prev, [id]: (prev[id] ? prev[id] + ', ' : '') + 'scored and completed' }));
      queryClient.invalidateQueries({ queryKey: ['admin_failed_sessions'] });
    } catch (e) {
      setResult(prev => ({ ...prev, [id]: (e as Error).message }));
    } finally {
      setBusyId(null);
    }
  };

  if (isLoading) return <LoadingRow />;
  if (error) return <ErrorPanel error={error} />;

  return (
    <Section
      title="Failed sessions, last 60 days"
      sub="Diagnosis joins the raw error the client logged before compressing it. CV failures are not repairable because the extracted text is never persisted (known product gap)."
    >
      {(data ?? []).length === 0 ? <EmptyNote>No failures in the window</EmptyNote> : (
        <TableWrap>
          <thead><tr><Th>When</Th><Th>User</Th><Th>Kind</Th><Th>What the user was told</Th><Th>Raw error</Th><Th>Evidence</Th><Th>Repair</Th></tr></thead>
          <tbody>
            {(data ?? []).map(s => (
              <tr key={s.id}>
                <Td className="whitespace-nowrap">{agoLabel(s.created_at)}</Td>
                <Td><UserLink id={s.user_id} label={s.email} /></Td>
                <Td>{s.type}</Td>
                <Td className="max-w-[220px]">{s.failure_reason ?? 'none recorded'}</Td>
                <Td className="max-w-[220px] text-rose-600">{s.raw_error ?? 'no raw event captured'}</Td>
                <Td className="whitespace-nowrap">{s.transcript_items} answers · {s.recordings} clips</Td>
                <Td>
                  {s.retry_kind === 'score' && (
                    <Button size="sm" variant="primary" busy={busyId === s.id} onClick={() => repair(s.id, 'score')}>Rescore</Button>
                  )}
                  {s.retry_kind === 'transcribe' && (
                    <Button size="sm" variant="primary" busy={busyId === s.id} onClick={() => repair(s.id, 'transcribe')}>Re-transcribe and score</Button>
                  )}
                  {s.retry_kind === 'none' && <span className="text-[11px] text-stone-400">Not repairable</span>}
                  {result[s.id] && <p className="text-[10.5px] text-stone-500 mt-1 max-w-[160px]">{result[s.id]}</p>}
                  <Link to={`/sessions/${s.id}`} className="block text-[11px] text-indigo-600 mt-1">Open</Link>
                </Td>
              </tr>
            ))}
          </tbody>
        </TableWrap>
      )}
    </Section>
  );
};

const OrphansView: React.FC = () => {
  const { data, error, isLoading } = useRpc<any[]>('admin_orphaned_recordings');
  const [open, setOpen] = useState<string | null>(null);
  const { data: clips } = useSel<any[]>(['orphan-clips', open], () =>
    supabase.from('session_recordings').select('question_index, storage_path, audio_storage_path, size_bytes, duration_ms, mime_type').eq('recording_session_id', open!).order('question_index'),
    { enabled: !!open });
  const [playUrl, setPlayUrl] = useState<string | null>(null);

  const play = async (path: string) => {
    const { data: signed, error: e } = await supabase.storage.from('session-recordings').createSignedUrl(path, 900);
    if (e) { setPlayUrl(null); return; }
    setPlayUrl(signed?.signedUrl ?? null);
  };

  if (isLoading) return <LoadingRow />;
  if (error) return <ErrorPanel error={error} />;

  return (
    <Section
      title="Orphaned recording runs"
      sub="Recordings no session row points at: the user recorded answers and the app lost the run. Storage still holds the video."
    >
      {(data ?? []).length === 0 ? <EmptyNote>No orphans</EmptyNote> : (
        <TableWrap>
          <thead><tr><Th>Run</Th><Th>User</Th><Th>Clips</Th><Th>Size</Th><Th>When</Th><Th></Th></tr></thead>
          <tbody>
            {(data ?? []).map(o => (
              <React.Fragment key={o.recording_session_id}>
                <tr>
                  <Td className="text-stone-400">{String(o.recording_session_id).slice(0, 8)}</Td>
                  <Td><UserLink id={o.user_id} label={o.email} /></Td>
                  <Td className="tabular-nums">{o.clips}</Td>
                  <Td className="tabular-nums">{fmtBytes(o.bytes)}</Td>
                  <Td className="whitespace-nowrap">{agoLabel(o.last_at)}</Td>
                  <Td><Button size="sm" onClick={() => setOpen(open === o.recording_session_id ? null : o.recording_session_id)}>
                    {open === o.recording_session_id ? 'Hide clips' : 'Clips'}
                  </Button></Td>
                </tr>
                {open === o.recording_session_id && (
                  <tr><Td colSpan={6}>
                    <div className="space-y-1 py-1">
                      {(clips ?? []).map((c, i) => (
                        <div key={i} className="flex items-center gap-3 text-[12px]">
                          <span className="text-stone-500">Question {Number(c.question_index) + 1}</span>
                          <span className="text-stone-400">{fmtBytes(c.size_bytes)} · {Math.round((c.duration_ms ?? 0) / 1000)}s</span>
                          <Button size="sm" onClick={() => play(c.storage_path)}>Play</Button>
                        </div>
                      ))}
                      {playUrl && <video controls src={playUrl} className="mt-2 max-w-md rounded-md border border-stone-200" />}
                    </div>
                  </Td></tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </TableWrap>
      )}
    </Section>
  );
};

const StuckView: React.FC = () => {
  const { data, error, isLoading } = useRpc<any[]>('admin_stuck_sessions');
  if (isLoading) return <LoadingRow />;
  if (error) return <ErrorPanel error={error} />;
  return (
    <Section title="Stuck in processing" sub="Older than 15 minutes. The reap-stuck-sessions cron flips these to failed every 15 minutes; anything shown here has outlived it.">
      {(data ?? []).length === 0 ? <EmptyNote>Nothing stuck</EmptyNote> : (
        <TableWrap>
          <thead><tr><Th>Session</Th><Th>User</Th><Th>Type</Th><Th>Started</Th></tr></thead>
          <tbody>
            {(data ?? []).map(s => (
              <tr key={s.id}>
                <Td><Link to={`/sessions/${s.id}`} className="text-indigo-600 hover:underline">{s.title || s.type}</Link></Td>
                <Td><UserLink id={s.user_id} label={s.email} /></Td>
                <Td>{s.type}</Td>
                <Td>{fmtDateTime(s.created_at)}</Td>
              </tr>
            ))}
          </tbody>
        </TableWrap>
      )}
    </Section>
  );
};

const PartialView: React.FC = () => {
  const { data, error, isLoading } = useRpc<any[]>('admin_partial_links');
  if (isLoading) return <LoadingRow />;
  if (error) return <ErrorPanel error={error} />;
  return (
    <Section title="Partially linked runs" sub="Sessions holding fewer linked recordings than their run produced; the report is missing video it should have.">
      {(data ?? []).length === 0 ? <EmptyNote>Every run fully linked</EmptyNote> : (
        <TableWrap>
          <thead><tr><Th>Session</Th><Th>User</Th><Th>Linked</Th><Th>Recorded</Th><Th>When</Th></tr></thead>
          <tbody>
            {(data ?? []).map(s => (
              <tr key={s.id}>
                <Td><Link to={`/sessions/${s.id}`} className="text-indigo-600 hover:underline">{s.type}</Link></Td>
                <Td><UserLink id={s.user_id} /></Td>
                <Td className="tabular-nums">{s.linked}</Td>
                <Td className="tabular-nums">{s.recorded}</Td>
                <Td className="whitespace-nowrap">{agoLabel(s.created_at)}</Td>
              </tr>
            ))}
          </tbody>
        </TableWrap>
      )}
    </Section>
  );
};

export const RevokeShareDialog: React.FC<{ sessionId: string | null; onClose: () => void }> = ({ sessionId, onClose }) => (
  <ConfirmDialog
    open={!!sessionId} onClose={onClose}
    title="Revoke shared report" tone="danger" confirmLabel="Revoke"
    body={<>The public share link stops working immediately.</>}
    onConfirm={async () => {
      const { error } = await supabase.rpc('admin_revoke_share', { p_session_id: sessionId });
      if (error) throw new Error(error.message);
      queryClient.invalidateQueries();
    }}
  />
);
